import { useQuery } from "react-query";

import * as reviewService from "api/services/reviewsService";
import { reconciliationTypes } from "constants/reconciliationTypes";
import { useMunicipalContext } from "contexts/MunicipalContext";

/**
 * Store server's state of Reconciliations results
 *
 * @param {number} year
 * @param {string} type
 * @returns The query object returned by the useQuery hook
 */
export const useReconciliationReviews = (year, type) => {
  const calculateNetValues = (data) => ({
    netRevenues:
      data.payments + data.interestEarned + data.proceedsFromDisposal,
    netTransfers: data.transferredIn - data.transferredOut,
    netExpenditures:
      data.airport +
      data.broadband +
      data.brownfield +
      data.capacityBldg +
      data.commEnergy +
      data.culture +
      data.disaster +
      data.fireStation +
      data.recreation +
      data.roadsBridges +
      data.shortline +
      data.shortsea +
      data.solidWaste +
      data.sport +
      data.transit +
      data.tourism +
      data.wastewater +
      data.water,
  });

  const { getMunicipalityName } = useMunicipalContext();

  return useQuery(
    ["reconciliationReviews", year, type],
    async () => {
      if (type === reconciliationTypes.changes) {
        return reviewService.getReconciliationChanges(year);
      }
      if (type === reconciliationTypes.current) {
        return reviewService.getReconciliationCurrent(year);
      }
      if (type === reconciliationTypes.cumulative) {
        return reviewService.getReconciliationCumulative(year);
      }
      throw new Error(`Unknown reconciliation type: ${type}`);
    },
    {
      select: (response) => {
        if (type === reconciliationTypes.changes) {
          const transformedData = response.data.map((item) => {
            let { amount } = item;

            if (
              item.reason &&
              item.reason.toLowerCase().includes("expenditures")
            ) {
              amount *= -1;
            }

            return {
              ...item,
              amount,
              municipalityName: getMunicipalityName(item.municipalityId),
            };
          });
          return {
            data: transformedData,
            isAudited: response.data.length > 0,
            hasChanges: response.data.length > 0,
            netRevenues: null,
            netTransfers: null,
            netExpenditures: null,
          };
        }

        if (
          type === reconciliationTypes.current ||
          type === reconciliationTypes.cumulative
        ) {
          const { data } = response;
          return {
            audited: data.audited
              ? {
                  ...data.audited,
                  ...calculateNetValues(data.audited),
                }
              : null,
            current: data.current
              ? {
                  ...data.current,
                  ...calculateNetValues(data.current),
                }
              : null,
            changes: data.changes
              ? {
                  ...data.changes,
                  ...calculateNetValues(data.changes),
                }
              : null,
            variance: data.variance
              ? {
                  ...data.variance,
                  ...calculateNetValues(data.variance),
                }
              : null,
            isAudited: data.isAudited,
            hasChanges: data.hasChanges,
          };
        }

        return {
          data: [],
          isAudited: null,
          hasChanges: null,
          netRevenues: null,
          netTransfers: null,
          netExpenditures: null,
        };
      },
      enabled: !!year && !!type,
    }
  );
};

const getProjectCategory = (reconciliationData) => {
  if (reconciliationData.broadband !== 0) return 1;
  if (reconciliationData.brownfield !== 0) return 2;
  if (reconciliationData.capacityBldg !== 0) return 3;
  if (reconciliationData.commEnergy !== 0) return 4;
  if (reconciliationData.culture !== 0) return 5;
  if (reconciliationData.disaster !== 0) return 6;
  if (reconciliationData.water !== 0) return 7;
  if (reconciliationData.fireStation !== 0) return 8;
  if (reconciliationData.roadsBridges !== 0) return 9;
  if (reconciliationData.transit !== 0) return 10;
  if (reconciliationData.recreation !== 0) return 11;
  if (reconciliationData.airport !== 0) return 12;
  if (reconciliationData.shortline !== 0) return 13;
  if (reconciliationData.shortsea !== 0) return 14;
  if (reconciliationData.solidWaste !== 0) return 15;
  if (reconciliationData.sport !== 0) return 16;
  if (reconciliationData.tourism !== 0) return 17;
  if (reconciliationData.wastewater !== 0) return 18;

  return null;
};

/**
 * Store server's state of Reconciliations result
 *
 * @param {number} reconciliationId
 * @returns The query object returned by the useQuery hook
 */
export const useReconciliationChangeReviewId = (reconciliationId) => {
  const { getMunicipalityName } = useMunicipalContext();

  return useQuery(
    ["reconciliationChangeReviewId", reconciliationId],
    () => reviewService.getReconciliationChangeById(reconciliationId),
    {
      enabled: !!reconciliationId,
      select: (response) => {
        if (!response?.data) return null;

        let { amount } = response.data;

        if (response.data.reason?.toLowerCase().includes("expenditures")) {
          amount *= -1;
        }

        return {
          ...response.data,
          amount,
          projectCategory: getProjectCategory(response.data),
          municipalityName: getMunicipalityName(response.data.municipalityId),
        };
      },
    }
  );
};
